"use client";

import { apiClient } from "@shared/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface PropertyPageGroup {
  id: string;
  propertyId: string;
  name: string;
  color: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { pages: number };
}

const basePath = (propertyId: string) =>
  apiClient.properties[":id"]["page-groups"] as unknown as {
    $get: (opts: { param: { id: string } }) => Promise<Response>;
    $post: (opts: {
      param: { id: string };
      json: { name: string; color?: string | null };
    }) => Promise<Response>;
    [":groupId"]: {
      $get: (opts: { param: { id: string; groupId: string } }) => Promise<Response>;
      $patch: (opts: {
        param: { id: string; groupId: string };
        json: { name?: string; color?: string | null };
      }) => Promise<Response>;
      $delete: (opts: { param: { id: string; groupId: string } }) => Promise<Response>;
      pages: {
        $post: (opts: {
          param: { id: string; groupId: string };
          json: { propertyPageIds: string[] };
        }) => Promise<Response>;
        $delete: (opts: {
          param: { id: string; groupId: string };
          json: { propertyPageIds: string[] };
        }) => Promise<Response>;
        $put: (opts: {
          param: { id: string; groupId: string };
          json: { propertyPageIds: string[] };
        }) => Promise<Response>;
      };
    };
  };

export const propertyPageGroupsQueryKey = (propertyId: string) =>
  ["properties", propertyId, "page-groups"] as const;

export const propertyPageGroupQueryKey = (propertyId: string, groupId: string) =>
  ["properties", propertyId, "page-groups", groupId] as const;

export function usePropertyPageGroupsQuery(propertyId: string) {
  return useQuery({
    queryKey: propertyPageGroupsQueryKey(propertyId),
    queryFn: async () => {
      const path = basePath(propertyId);
      const response = await path.$get({ param: { id: propertyId } });
      if (!response.ok) {
        const err = (await response.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to fetch page groups");
      }
      return response.json() as Promise<PropertyPageGroup[]>;
    },
    enabled: !!propertyId,
  });
}

export function useCreatePropertyPageGroupMutation(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; color?: string | null }) => {
      const path = basePath(propertyId);
      const response = await path.$post({
        param: { id: propertyId },
        json: { name: input.name, color: input.color ?? null },
      });
      if (!response.ok) {
        const err = (await response.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to create page group");
      }
      return response.json() as Promise<PropertyPageGroup>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertyPageGroupsQueryKey(propertyId) });
    },
  });
}

export function usePropertyPageGroupQuery(propertyId: string, groupId: string, enabled = true) {
  return useQuery({
    queryKey: propertyPageGroupQueryKey(propertyId, groupId),
    queryFn: async () => {
      const path = basePath(propertyId)[":groupId"];
      const response = await path.$get({ param: { id: propertyId, groupId } });
      if (!response.ok) {
        if (response.status === 404) return null;
        const err = (await response.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to fetch page group");
      }
      return response.json() as Promise<
        PropertyPageGroup & { pages?: { propertyPageId: string }[] }
      >;
    },
    enabled: !!propertyId && !!groupId && enabled,
  });
}

export function useUpdatePropertyPageGroupMutation(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      groupId,
      name,
      color,
    }: {
      groupId: string;
      name?: string;
      color?: string | null;
    }) => {
      const path = basePath(propertyId)[":groupId"];
      const response = await path.$patch({
        param: { id: propertyId, groupId },
        json: { name, color },
      });
      if (!response.ok) {
        const err = (await response.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to update page group");
      }
      return response.json() as Promise<PropertyPageGroup>;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: propertyPageGroupsQueryKey(propertyId),
      });
      queryClient.invalidateQueries({
        queryKey: propertyPageGroupQueryKey(propertyId, variables.groupId),
      });
    },
  });
}

export function useDeletePropertyPageGroupMutation(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: string) => {
      const path = basePath(propertyId)[":groupId"];
      const response = await path.$delete({ param: { id: propertyId, groupId } });
      if (!response.ok && response.status !== 204) {
        const err = (await response.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to delete page group");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertyPageGroupsQueryKey(propertyId) });
    },
  });
}

export function useSetPageGroupPagesMutation(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      groupId,
      propertyPageIds,
    }: {
      groupId: string;
      propertyPageIds: string[];
    }) => {
      const path = basePath(propertyId)[":groupId"].pages;
      const response = await path.$put({
        param: { id: propertyId, groupId },
        json: { propertyPageIds },
      });
      if (!response.ok) {
        const err = (await response.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to set pages");
      }
      return response.json() as Promise<PropertyPageGroup & { pages?: unknown[] }>;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: propertyPageGroupsQueryKey(propertyId),
      });
      queryClient.invalidateQueries({
        queryKey: propertyPageGroupQueryKey(propertyId, variables.groupId),
      });
    },
  });
}

export function useAddPageGroupPagesMutation(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      groupId,
      propertyPageIds,
    }: {
      groupId: string;
      propertyPageIds: string[];
    }) => {
      const path = basePath(propertyId)[":groupId"].pages;
      const response = await path.$post({
        param: { id: propertyId, groupId },
        json: { propertyPageIds },
      });
      if (!response.ok) {
        const err = (await response.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to add pages");
      }
      return response.json() as Promise<PropertyPageGroup & { pages?: unknown[] }>;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: propertyPageGroupsQueryKey(propertyId),
      });
      queryClient.invalidateQueries({
        queryKey: propertyPageGroupQueryKey(propertyId, variables.groupId),
      });
    },
  });
}

export function useRemovePageGroupPagesMutation(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      groupId,
      propertyPageIds,
    }: {
      groupId: string;
      propertyPageIds: string[];
    }) => {
      const path = basePath(propertyId)[":groupId"].pages;
      const response = await path.$delete({
        param: { id: propertyId, groupId },
        json: { propertyPageIds },
      });
      if (!response.ok) {
        const err = (await response.json()) as { error?: string };
        throw new Error(err.error ?? "Failed to remove pages");
      }
      return response.json() as Promise<PropertyPageGroup & { pages?: unknown[] }>;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: propertyPageGroupsQueryKey(propertyId),
      });
      queryClient.invalidateQueries({
        queryKey: propertyPageGroupQueryKey(propertyId, variables.groupId),
      });
    },
  });
}
